const cloneDeep = require('lodash.clonedeep');
const { MEASURE_SCORES, FLO_PREC, SUPPORTED_MEASURES } = require('./constants/common');

const has = Object.prototype.hasOwnProperty;

/**
 * Round given number with precision to the hundredths place (to the second digit after decimal point).
 * Examples:
 * toHundredthsPrecision(1.444444) === 1.44
 * toHundredthsPrecision(1.445555) === 1.45
 * toHundredthsPrecision(1.454545) === 1.46
 *
 * @param {Number} num - number to round
 * @returns {Number} - rounded input number
 */
const toHundredthsPrecision = (num) => Math.round(Math.round(num * 1000) / 10) / 100;

/**
 * Get measureScores from the result.
 *
 * @param {Object} result - Assessment result
 * @returns {Object} Measure scores objects by measure.
 */
const getScoresInfo = (result) => {
    if (has.call(result, MEASURE_SCORES)) {
        const resultMeasureScore = cloneDeep(result[MEASURE_SCORES]);
        return Object.entries(result[MEASURE_SCORES])
            .reduce((measureScores, [measure, scoreObj]) => ({
                ...measureScores,
                [measure]: {
                    ...scoreObj,
                    isDiscontinueRuleMet: result.isDiscontinueRuleMet || false,
                    serverCreateDate: result.serverCreateDate,
                    serverModDate: result.serverModDate,
                    clientCreateDate: result.clientCreateDate,
                    clientModDate: result.clientModDate,
                },
            }
            ), resultMeasureScore);
    }
    return {
        [result.measure]: {
            rawScore: result.rawScore,
            interpretation: result.interpretation,
            isDiscontinueRuleMet: result.isDiscontinueRuleMet || false,
            serverCreateDate: result.serverCreateDate,
            serverModDate: result.serverModDate,
            clientCreateDate: result.clientCreateDate,
            clientModDate: result.clientModDate,
        },
    };
};

/**
 * Extract measure specific rawScore from given measureScore obj.
 */
const getRawScore = (measureName, measureScoreObj) => (
    measureName === FLO_PREC ? measureScoreObj.rawScore / 100 : measureScoreObj.rawScore
);

/**
 * Group information about student scores by grade and period.
 */
const getScoresInfoByWindow = (studentResults) => {
    if (!Array.isArray(studentResults)) {
        throw Error('Results array is expected as function\'s argument');
    }
    return studentResults.reduce((measureScoresGroupByWindow, result) => {
        const {
            gradeLevel: grade,
            period,
            isValid,
        } = result;

        if (isValid && SUPPORTED_MEASURES.includes(result.measure)) {
            return {
                ...measureScoresGroupByWindow,
                [grade]: {
                    ...(measureScoresGroupByWindow[grade]),
                    [period]: {
                        ...((measureScoresGroupByWindow[grade] || {})[period]),
                        ...getScoresInfo(result),
                    },
                },
            };
        }
        if (!isValid) {
            console.warn(`Invalidated result ${result.resultId} cannot be used`);
        }
        return measureScoresGroupByWindow;
    }, {});
};

module.exports = {
    getScoresInfoByWindow,
    getRawScore,
    toHundredthsPrecision,
    has,
};
